<template>
  <div v-if="files && files.length" class="existing-file-list mb-2">
    <small class="text-muted d-block mb-1">File saat ini</small>
    <div
      v-for="(file, idx) in files"
      :key="idx"
      class="existing-file-item"
    >
      <div v-if="isImage(file) && getImageUrl(file) && !failedImages[idx]" class="existing-file-thumb">
        <img :src="getImageUrl(file)" :alt="file.usrName" class="thumb-img" @error="onImgError(idx)" />
      </div>
      <div v-else class="existing-file-icon">
        <feather-icon :icon="fileIcon(file)" size="16" />
      </div>

      <div class="existing-file-info">
        <template v-if="renamingIndex === idx">
          <b-form-input
            v-model="renameValue"
            size="sm"
            autofocus
            autocomplete="off"
            spellcheck="false"
            @keyup.enter="confirmRename(file)"
            @keyup.esc="cancelRename"
          />
        </template>
        <template v-else>
          <div class="existing-file-name" :title="file.usrName || file.name">{{ file.usrName || file.name || 'File' }}</div>
          <div v-if="formatSize(file.size)" class="existing-file-size">{{ formatSize(file.size) }}</div>
        </template>
      </div>

      <template v-if="renamingIndex === idx">
        <b-button
          v-ripple.400="'rgba(40, 199, 111, 0.15)'"
          variant="flat-success"
          size="sm"
          class="btn-icon"
          v-b-tooltip.hover.top="'Simpan nama'"
          @click="confirmRename(file)"
        >
          <feather-icon icon="CheckIcon" size="14" />
        </b-button>
        <b-button
          v-ripple.400="'rgba(130, 134, 139, 0.15)'"
          variant="flat-secondary"
          size="sm"
          class="btn-icon"
          v-b-tooltip.hover.top="'Batal'"
          @click="cancelRename"
        >
          <feather-icon icon="XIcon" size="14" />
        </b-button>
      </template>
      <template v-else>
        <b-button
          v-ripple.400="'rgba(130, 134, 139, 0.15)'"
          variant="flat-secondary"
          size="sm"
          class="btn-icon"
          v-b-tooltip.hover.top="'Rename'"
          @click="startRename(file, idx)"
        >
          <feather-icon icon="Edit2Icon" size="14" />
        </b-button>
        <b-button
          v-ripple.400="'rgba(115, 103, 240, 0.15)'"
          variant="flat-primary"
          size="sm"
          class="btn-icon existing-file-download"
          :disabled="downloadingIndex === idx"
          v-b-tooltip.hover.top="'Download'"
          @click="download(file, idx)"
        >
          <b-spinner v-if="downloadingIndex === idx" small />
          <feather-icon v-else icon="DownloadIcon" size="15" />
        </b-button>
        <b-button
          v-ripple.400="'rgba(234, 84, 85, 0.15)'"
          variant="flat-danger"
          size="sm"
          class="btn-icon"
          v-b-tooltip.hover.top="'Delete'"
          @click="confirmDeleteFile(file, idx)"
        >
          <feather-icon icon="Trash2Icon" size="14" />
        </b-button>
      </template>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BFormInput, VBTooltip } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { saveAs } from 'file-saver'

export default {
  name: 'ExistingFileList',
  components: { BButton, BSpinner, BFormInput },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      downloadingIndex: null,
      renamingIndex: null,
      renameValue: '',
      failedImages: {},
    }
  },
  methods: {
    isImage(file) {
      const type = (file?.type || '').toLowerCase()
      const ext = (file?.usrName || file?.name || '').split('.').pop().toLowerCase()
      return type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)
    },
    getImageUrl(file) {
      const link = file?.link || file?.url || file?.file_url
      if (!link || typeof link !== 'string' || link.trim() === '' || link === 'null') return ''
      const cleanUrl = link.trim()
      if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://') || cleanUrl.startsWith('data:')) {
        return cleanUrl
      }
      const baseUrl = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '')
      return `${baseUrl}/${cleanUrl.replace(/^\/+/, '')}`
    },
    onImgError(idx) {
      this.$set(this.failedImages, idx, true)
    },
    fileIcon(file) {
      const type = (file?.type || '').toLowerCase()
      const ext = (file?.usrName || file?.name || '').split('.').pop().toLowerCase()
      if (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) {
        return 'ImageIcon'
      }
      if (type.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) {
        return 'FilmIcon'
      }
      if (['xls', 'xlsx', 'csv'].includes(ext)) {
        return 'GridIcon'
      }
      return 'FileTextIcon'
    },
    formatSize(bytes) {
      const size = Number(bytes)
      if (!Number.isFinite(size) || size <= 0) return ''
      const units = ['B', 'KB', 'MB', 'GB']
      let value = size
      let unitIndex = 0
      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex += 1
      }
      return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
    },
    startRename(file, idx) {
      this.renamingIndex = idx
      this.renameValue = file.usrName || file.name || ''
    },
    cancelRename() {
      this.renamingIndex = null
      this.renameValue = ''
    },
    confirmRename(file) {
      const trimmed = this.renameValue.trim()
      if (!trimmed) {
        this.$notif('Error', 'File name cannot be empty.', 'danger')
        return
      }
      file.usrName = trimmed
      if (file.objItem) {
        file.objItem.usrName = trimmed
      }
      this.renamingIndex = null
      this.renameValue = ''
      this.$notif('Success', 'File name updated. Click Save on the form to save changes.', 'success')
    },
    async download(file, idx) {
      if (!file?.link) return
      this.downloadingIndex = idx
      try {
        const rawUrl = (file.link || '').trim()
        const fileUrl = rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('data:')
          ? rawUrl
          : `${(process.env.VUE_APP_API_URL || '').replace(/\/+$/, '')}/${rawUrl.replace(/^\/+/, '')}`
        const response = await fetch(fileUrl)
        if (!response.ok) throw new Error('Download failed')
        const blob = await response.blob()
        saveAs(blob, file.usrName || file.name || 'download')
      } catch (err) {
        console.error(err)
        this.$notif('Error', 'Failed to download file. Please try again.', 'danger')
      } finally {
        this.downloadingIndex = null
      }
    },
    confirmDeleteFile(file, idx) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this item.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.files.splice(idx, 1)
            this.$emit('delete', { file, index: idx })
            this.$notif(
              'Success',
              'File removed from list. Click Save on the form to save changes.',
              'success'
            )
          }
        })
    },
  },
}
</script>

<style scoped>
.existing-file-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  margin-bottom: 0.4rem;
  background: rgba(0, 0, 0, 0.015);
}

.existing-file-thumb {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
}

.existing-file-thumb img.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.existing-file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  flex-shrink: 0;
}

.existing-file-info {
  flex: 1 1 auto;
  min-width: 0;
}

.existing-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
}

.existing-file-size {
  font-size: 0.7rem;
  color: #b8c2cc;
}

.existing-file-download {
  flex-shrink: 0;
}
</style>
